import React from "react";
import {
  SiSteam,
  SiEpicgames,
  SiPlaystation,
  SiEa,
  SiUbisoft,
  SiBattledotnet,
  SiRockstargames,
  SiGogdotcom,
} from "react-icons/si";
import { FaXbox, FaGamepad } from "react-icons/fa6";
import { BsNintendoSwitch } from "react-icons/bs";

/**
 * Normalizes platform names, slugs, or database identifiers
 * to a standardized platform key.
 */
export const normalizePlatform = (platform) => {
  if (!platform) return "fallback";
  const raw = typeof platform === "object" && platform !== null
    ? (platform.name || platform.slug || "")
    : platform;
  const s = String(raw || "").toLowerCase().trim();

  if (s.includes("steam")) return "steam";
  if (s.includes("epic")) return "epic";
  if (s.includes("playstation") || s.includes("ps5") || s.includes("ps4") || s === "ps") return "playstation";
  if (s.includes("xbox")) return "xbox";
  if (s.includes("nintendo") || s.includes("switch")) return "nintendo";
  if (s === "ea" || s.includes("electronic arts") || s.includes("origin") || s.startsWith("ea ")) return "ea";
  if (s.includes("ubisoft") || s.includes("uplay")) return "ubisoft";
  if (s.includes("battle") || s.includes("bnet") || s.includes("blizzard")) return "battle-net";
  if (s.includes("rockstar") || s.includes("social club")) return "rockstar";
  if (s.includes("gog")) return "gog";

  return "fallback";
};

export const PLATFORM_ICON_MAP = {
  steam: SiSteam,
  epic: SiEpicgames,
  playstation: SiPlaystation,
  xbox: FaXbox,
  nintendo: BsNintendoSwitch,
  ea: SiEa,
  ubisoft: SiUbisoft,
  "battle-net": SiBattledotnet,
  rockstar: SiRockstargames,
  gog: SiGogdotcom,
  fallback: FaGamepad,
};

export const getPlatformIcon = (platform) => {
  const key = normalizePlatform(platform);
  return PLATFORM_ICON_MAP[key] || PLATFORM_ICON_MAP.fallback;
};

/**
 * Centralized, reusable PlatformIcon component.
 * Renders lightweight vector brand icons for all supported gaming platforms.
 */
export const PlatformIcon = ({
  platform,
  size = 16,
  className = "",
  title,
  ...props
}) => {
  const IconComponent = getPlatformIcon(platform);
  const platformLabel = typeof platform === "string" ? platform : "Platform";
  const iconTitle = title !== undefined ? title : platformLabel;

  return (
    <IconComponent
      size={size}
      className={`shrink-0 ${className}`}
      aria-label={iconTitle || undefined}
      title={iconTitle || undefined}
      {...props}
    />
  );
};

export const getPlatformAccountType = (platformName) => {
  if (!platformName) return "Account";
  const raw = typeof platformName === "object" && platformName !== null
    ? (platformName.account_type_label || platformName.name || platformName.slug || "")
    : platformName;
  const name = String(raw || "").toLowerCase();
  if (name.includes("steam")) return "Steam Account";
  if (name.includes("rockstar")) return "Rockstar Account";
  if (name.includes("ea")) return "EA Account";
  if (name.includes("ubisoft")) return "Ubisoft Account";
  if (name.includes("epic")) return "Epic Games Account";
  if (name.includes("gog")) return "GOG Account";
  if (name.includes("battle.net") || name.includes("battlenet")) return "Battle.net Account";
  if (name.includes("playstation")) return "PlayStation Account";
  if (name.includes("xbox")) return "Xbox Account";
  if (name.includes("nintendo")) return "Nintendo Account";
  if (name.includes("review") || name.includes("test")) return "Account (Review)";
  return `${raw || platformName} Account`;
};

export const PLATFORM_CONFIGS = [
  { name: "Steam", slug: "steam", icon: SiSteam, accountType: "Steam Account", launcher: "Steam" },
  { name: "Rockstar Games", slug: "rockstar-games", icon: SiRockstargames, accountType: "Rockstar Account", launcher: "Rockstar Games Launcher" },
  { name: "EA", slug: "ea", icon: SiEa, accountType: "EA Account", launcher: "EA App" },
  { name: "Ubisoft", slug: "ubisoft", icon: SiUbisoft, accountType: "Ubisoft Account", launcher: "Ubisoft Connect" },
  { name: "Epic Games", slug: "epic-games", icon: SiEpicgames, accountType: "Epic Games Account", launcher: "Epic Games Launcher" },
  { name: "GOG", slug: "gog", icon: SiGogdotcom, accountType: "GOG Account", launcher: "GOG Galaxy" },
  { name: "Battle.net", slug: "battle-net", icon: SiBattledotnet, accountType: "Battle.net Account", launcher: "Battle.net Desktop App" },
  { name: "PlayStation", slug: "playstation", icon: SiPlaystation, accountType: "PlayStation Account", launcher: "PlayStation Network" },
  { name: "Xbox", slug: "xbox", icon: FaXbox, accountType: "Xbox Account", launcher: "Xbox Console / App" },
  { name: "Nintendo", slug: "nintendo", icon: BsNintendoSwitch, accountType: "Nintendo Account", launcher: "Nintendo eShop" },
];

export const getPlatformLogo = (platformName) => {
  return getPlatformIcon(platformName);
};

/**
 * Centralized PlatformBadge component.
 * Renders vector PlatformIcon alongside the platform name with customizable sizing.
 */
export const PlatformBadge = ({ platform, showName = true, className = "", imgSize = "w-4 h-4", iconSize }) => {
  const name = typeof platform === "object" && platform !== null
    ? (platform.name || platform.slug || "Steam")
    : (platform || "Steam");

  const IconComponent = getPlatformIcon(platform);

  return (
    <div className={`inline-flex items-center gap-1.5 select-none ${className}`}>
      <IconComponent className={`${imgSize} shrink-0`} size={iconSize} />
      {showName && (
        <span className="text-xs font-semibold">{name}</span>
      )}
    </div>
  );
};

export default PlatformBadge;
